import { Logger } from './core/logger';
import { ConsoleProvider } from './providers/console';
import { PostHogLoggingProvider } from './providers/posthog';
import { DatadogLoggingProvider } from './providers/datadog';
import { getPartnerData } from 'app/utils/localStorageHandler/partnerData';

export type {
  LogPayload,
  LogLevel,
  ErrorType,
  Tags,
  TagsResolver,
  LoggerProvider,
} from './core/types';

function deriveModule(pathname: string): string {
  if (pathname.startsWith('/agent/')) return 'agent';
  if (pathname.startsWith('/compliance/')) return 'compliance';
  if (pathname.startsWith('/office-admin/')) return 'officeAdmin';
  if (pathname.startsWith('/admin/')) return 'admin';
  return 'unknown';
}

const logger = Logger.getInstance();

logger.setDefaultTags({
  environment: import.meta.env.VITE_NODE_ENV ?? 'unknown',
});

logger.registerTagsResolver(() => {
  const partner = getPartnerData();
  return {
    module: deriveModule(window.location.pathname),
    ...(partner?._id && { partner: partner._id }),
  };
});

logger.registerProvider(new ConsoleProvider());

if (import.meta.env.VITE_POSTHOG_ENABLED === 'true') {
  logger.registerProvider(new PostHogLoggingProvider());
}

if (
  import.meta.env.VITE_DATADOG_RUM_ENABLED === 'true' ||
  import.meta.env.VITE_DATADOG_LOGS_ENABLED === 'true'
) {
  logger.registerProvider(new DatadogLoggingProvider());
}

if (import.meta.env.DEV) {
  (window as any).__loggerTest = {
    // JS errors
    jsError: (module: string = 'agent') =>
      logger.captureException(
        new Error('Test JS error'),
        { component: 'DevTest' },
        { module, feature: 'test' }
      ),

    // API errors
    apiError: (module: string = 'agent') =>
      logger.log({
        type: 'API_ERROR',
        level: 'error',
        message: 'GET /api/test → 500',
        error: new Error('Internal Server Error'),
        tags: {
          module,
          endpoint: '/api/test',
          method: 'GET',
          status_code: '500',
        },
      }),

    // Module-specific helpers
    agentError: () =>
      logger.log({
        type: 'API_ERROR',
        level: 'error',
        message: 'GET /api/agent/test → 500',
        error: new Error('Agent module error'),
        tags: {
          module: 'agent',
          endpoint: '/api/agent/test',
          method: 'GET',
          status_code: '500',
        },
      }),
    complianceError: () =>
      logger.log({
        type: 'API_ERROR',
        level: 'error',
        message: 'GET /api/compliance/test → 500',
        error: new Error('Compliance module error'),
        tags: {
          module: 'compliance',
          endpoint: '/api/compliance/test',
          method: 'GET',
          status_code: '500',
        },
      }),

    validationError: (module: string = 'agent') =>
      logger.log({
        type: 'VALIDATION_ERROR',
        level: 'warn',
        message: 'Test validation error',
        tags: { module, feature: 'test' },
      }),
    businessError: (module: string = 'agent') =>
      logger.log({
        type: 'BUSINESS_ERROR',
        level: 'error',
        message: 'Test business rule violation',
        error: new Error('Business rule violated'),
        tags: { module, feature: 'test' },
      }),
  };
}

export default logger;
